import type { NotificationTypeDefinition } from './types';
import { NotificationAudience, NotificationCategory, NotificationChannel, ProviderRole } from './types';

// ─── Provider Notifications (9 types, IN_APP only) ──────────────────────────

export const PROVIDER_NOTIFICATIONS = {
  'provider.system.appointment-created': {
    key: 'provider.system.appointment-created',
    referenceCode: 'Provider_Sys_01',
    label: 'Appointment Created',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Provider receives notification when a new appointment is scheduled',
    defaultIntervals: ['immediately'],
  },
  'provider.system.appointment-cancelled': {
    key: 'provider.system.appointment-cancelled',
    referenceCode: 'Provider_Sys_02',
    label: 'Appointment Cancelled',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Provider receives notification when an appointment is cancelled',
    defaultIntervals: ['immediately'],
  },
  'provider.system.new-patient-triage': {
    key: 'provider.system.new-patient-triage',
    referenceCode: 'Provider_Sys_03',
    label: 'New Patient Triage',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Provider notified when a new patient is added to triage queue',
    defaultIntervals: ['immediately'],
  },
  'provider.system.new-patient-message': {
    key: 'provider.system.new-patient-message',
    referenceCode: 'Provider_Sys_04',
    label: 'New Patient Message',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Provider notified when a message is received from a patient',
    defaultIntervals: ['immediately'],
  },
  'provider.system.new-provider-message': {
    key: 'provider.system.new-provider-message',
    referenceCode: 'Provider_Sys_05',
    label: 'New Provider Message',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Provider notified when a message is received from another provider',
    defaultIntervals: ['immediately'],
  },
  'provider.system.hsat-results-available': {
    key: 'provider.system.hsat-results-available',
    referenceCode: 'Provider_Sys_06',
    label: 'HSAT Results Available',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.NP, ProviderRole.MD],
    description: 'Provider notified when new HSAT results are available for review',
    defaultIntervals: ['immediately'],
  },
  'provider.reminder.appointment': {
    key: 'provider.reminder.appointment',
    referenceCode: 'Provider_Rem_01',
    label: 'Appointment Reminder',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.REMINDER,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Reminder for upcoming appointment',
    defaultIntervals: ['24h-before', '15m-before'],
  },
  'provider.reminder.rpm-monthly': {
    key: 'provider.reminder.rpm-monthly',
    referenceCode: 'Provider_Rem_02',
    label: 'RPM Monthly Reminder',
    audience: NotificationAudience.PROVIDER,
    category: NotificationCategory.REMINDER,
    channels: [NotificationChannel.IN_APP],
    recipientRoles: [ProviderRole.CARE_COORDINATOR, ProviderRole.NP, ProviderRole.MD],
    description: 'Monthly reminder to monitor patient CPAP adherence',
    defaultIntervals: ['monthly'],
  },
} as const satisfies Record<string, NotificationTypeDefinition>;

// ─── Patient Notifications (30 types, multi-channel) ────────────────────────

const PATIENT_IN_APP_EMAIL_SMS = [
  NotificationChannel.IN_APP,
  NotificationChannel.EMAIL,
  NotificationChannel.SMS,
] as const;

const PATIENT_EMAIL_SMS_ONLY = [NotificationChannel.EMAIL, NotificationChannel.SMS] as const;

export const PATIENT_NOTIFICATIONS = {
  // ── Account ───────────────────────────────────────────────────────────────
  // Legacy key: predates the `<audience>.<category>.<name>` convention. Kept as
  // `patient-welcome` so the existing template path (emails/patient-welcome/…),
  // bot service, and Communication tag stay valid.
  'patient-welcome': {
    key: 'patient-welcome',
    referenceCode: 'Account_Welcome',
    label: 'Patient Welcome',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [NotificationChannel.EMAIL],
    description: 'Patient receives welcome email with a link to set their password',
    defaultIntervals: ['immediately'],
    essential: true,
  },
  'patient.account.pcp-referral-receipt': {
    key: 'patient.account.pcp-referral-receipt',
    referenceCode: 'Account_00',
    label: 'PCP Referral Receipt',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient receives confirmation of PCP referral',
    defaultIntervals: ['immediately'],
    essential: true,
  },
  'patient.account.screener-receipt': {
    key: 'patient.account.screener-receipt',
    referenceCode: 'Account_01',
    label: 'Screener Receipt',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient receives confirmation after completing screener',
    defaultIntervals: ['immediately'],
    essential: true,
  },
  'patient.system.validate-account': {
    key: 'patient.system.validate-account',
    referenceCode: 'Account_02',
    label: 'Validate Account',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient prompted to validate their account',
    defaultIntervals: ['immediately'],
    essential: true,
  },
  'patient.account.intake-appointment-scheduled': {
    key: 'patient.account.intake-appointment-scheduled',
    referenceCode: 'Account_07',
    label: 'Intake Appointment Scheduled',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'New patient is notified that their intake appointment has been scheduled',
    defaultIntervals: ['immediately'],
  },
  'patient.account.intake-questionnaire-reminder': {
    key: 'patient.account.intake-questionnaire-reminder',
    referenceCode: 'Account_08',
    label: 'Complete Your Intake Questionnaire',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.ACCOUNT,
    channels: [NotificationChannel.IN_APP],
    description: 'Patient is reminded to complete the intake questionnaire',
    defaultIntervals: ['immediately'],
    essential: true,
  },

  // ── System ────────────────────────────────────────────────────────────────
  'patient.system.incomplete-information': {
    key: 'patient.system.incomplete-information',
    referenceCode: 'Account_03',
    label: 'Incomplete Information',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified of incomplete account information',
    defaultIntervals: ['immediately'],
  },
  'patient.system.chart-update': {
    key: 'patient.system.chart-update',
    referenceCode: 'Account_04',
    label: 'Patient Chart Update',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when their chart is updated',
    defaultIntervals: ['immediately'],
  },
  'patient.system.new-provider-message': {
    key: 'patient.system.new-provider-message',
    referenceCode: 'Account_05',
    label: 'New Provider Message',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when a provider sends a message',
    defaultIntervals: ['immediately'],
  },
  'patient.system.new-provider-note': {
    key: 'patient.system.new-provider-note',
    referenceCode: 'Account_06',
    label: 'New Provider Note',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when a provider adds a note',
    defaultIntervals: ['immediately'],
  },
  'patient.system.appointment-created': {
    key: 'patient.system.appointment-created',
    referenceCode: 'System_05',
    label: 'Appointment Created',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    description: 'Patient notified when an appointment is booked for them',
    defaultIntervals: ['immediately'],
  },
  'patient.system.appointment-rescheduling': {
    key: 'patient.system.appointment-rescheduling',
    referenceCode: 'System_03',
    label: 'Appointment Rescheduling',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [NotificationChannel.IN_APP],
    description: 'Patient notified when an appointment is rescheduled',
    defaultIntervals: ['immediately'],
  },
  'patient.system.appointment-cancellation': {
    key: 'patient.system.appointment-cancellation',
    referenceCode: 'System_04',
    label: 'Appointment Cancellation',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when an appointment is cancelled',
    defaultIntervals: ['immediately'],
  },
  'patient.system.appointment-payment-required': {
    key: 'patient.system.appointment-payment-required',
    referenceCode: 'System_06',
    label: 'Appointment Payment Required',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.SYSTEM,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified that payment is required before appointment is confirmed',
    defaultIntervals: ['immediately'],
  },

  // ── HSAT ──────────────────────────────────────────────────────────────────
  'patient.hsat.results-available': {
    key: 'patient.hsat.results-available',
    referenceCode: 'HSAT_04',
    label: 'HSAT Results Available',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when HSAT results are available',
    defaultIntervals: ['immediately'],
  },
  'patient.hsat.ineligible-not-credentialed': {
    key: 'patient.hsat.ineligible-not-credentialed',
    referenceCode: 'HSAT_00a',
    label: 'Ineligible - Not Credentialed',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient marked ineligible for HSAT: provider not credentialed',
    defaultIntervals: ['immediately'],
  },
  'patient.hsat.ineligible-not-medically-necessary': {
    key: 'patient.hsat.ineligible-not-medically-necessary',
    referenceCode: 'HSAT_00b',
    label: 'Ineligible - Not Medically Necessary',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient marked ineligible for HSAT: not medically necessary',
    defaultIntervals: ['immediately'],
  },
  'patient.hsat.ineligible-recent-test': {
    key: 'patient.hsat.ineligible-recent-test',
    referenceCode: 'HSAT_00c',
    label: 'Ineligible - Recent Test',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient marked ineligible for HSAT: recent test on file',
    defaultIntervals: ['immediately'],
  },
  'patient.hsat.ineligible-insufficient': {
    key: 'patient.hsat.ineligible-insufficient',
    referenceCode: 'HSAT_00d',
    label: 'Ineligible - HSAT Insufficient',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient marked ineligible for HSAT: insufficient data',
    defaultIntervals: ['immediately'],
  },
  'patient.hsat.order-status-changed': {
    key: 'patient.hsat.order-status-changed',
    referenceCode: 'HSAT_05',
    label: 'HSAT Order Status Changed',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.HSAT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when HSAT order status changes',
    defaultIntervals: ['immediately'],
  },

  // ── DME ───────────────────────────────────────────────────────────────────
  'patient.dme.adherence-reminder': {
    key: 'patient.dme.adherence-reminder',
    referenceCode: 'DME_XX',
    label: 'DME Adherence Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.DME,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded about DME adherence',
    defaultIntervals: ['immediately'],
  },

  // ── Payment ───────────────────────────────────────────────────────────────
  'patient.payment.new-payment': {
    key: 'patient.payment.new-payment',
    referenceCode: 'Payment_01',
    label: 'New Payment Available',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.PAYMENT,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified when a new payment is available or due',
    defaultIntervals: ['immediately'],
  },
  'patient.payment.payment-receipt': {
    key: 'patient.payment.payment-receipt',
    referenceCode: 'Payment_02',
    label: 'Payment Receipt',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.PAYMENT,
    channels: [...PATIENT_EMAIL_SMS_ONLY],
    description: 'Patient receives payment confirmation receipt',
    defaultIntervals: ['immediately'],
    essential: true,
  },

  // ── Reminder ──────────────────────────────────────────────────────────────
  'patient.reminder.intake-appointment': {
    key: 'patient.reminder.intake-appointment',
    referenceCode: 'Reminder_01',
    label: 'Intake Appointment Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded of upcoming intake appointment',
    defaultIntervals: ['immediately', '3d-before', '24h-before', '15m-before'],
    maxAttempts: 4,
  },
  'patient.reminder.hsat': {
    key: 'patient.reminder.hsat',
    referenceCode: 'Reminder_02',
    label: 'HSAT Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded to complete HSAT',
    defaultIntervals: ['daily'],
    maxAttempts: 5,
  },
  'patient.reminder.hsat-final': {
    key: 'patient.reminder.hsat-final',
    referenceCode: 'Reminder_03',
    label: 'HSAT Final Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Final reminder for patient to complete HSAT',
    defaultIntervals: ['immediately'],
  },
  'patient.reminder.diagnostic-appointment': {
    key: 'patient.reminder.diagnostic-appointment',
    referenceCode: 'Reminder_04',
    label: 'Diagnostic Appointment Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded of upcoming diagnostic appointment',
    defaultIntervals: ['immediately', '3d-before', '24h-before', '15m-before'],
    maxAttempts: 4,
  },
  'patient.reminder.prescription-available': {
    key: 'patient.reminder.prescription-available',
    referenceCode: 'Reminder_05',
    label: 'Prescription Available',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient notified that a prescription is available',
    defaultIntervals: ['immediately'],
  },
  'patient.reminder.followup-30': {
    key: 'patient.reminder.followup-30',
    referenceCode: 'Reminder_06a',
    label: '30-Day Follow-up Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded of 30-day follow-up appointment',
    defaultIntervals: ['immediately', '3d-before', '24h-before', '15m-before'],
    maxAttempts: 4,
  },
  'patient.reminder.followup-90': {
    key: 'patient.reminder.followup-90',
    referenceCode: 'Reminder_06b',
    label: '90-Day Follow-up Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded of 90-day follow-up appointment',
    defaultIntervals: ['immediately', '3d-before', '24h-before', '15m-before'],
    maxAttempts: 4,
  },
  'patient.reminder.followup-150': {
    key: 'patient.reminder.followup-150',
    referenceCode: 'Reminder_06c',
    label: '150-Day Follow-up Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded of 150-day follow-up appointment',
    defaultIntervals: ['immediately', '3d-before', '24h-before', '15m-before'],
    maxAttempts: 4,
  },
  'patient.reminder.appointment-scheduling': {
    key: 'patient.reminder.appointment-scheduling',
    referenceCode: 'Reminder_06d',
    label: 'Appointment Scheduling Reminder',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.REMINDER,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient reminded to schedule an appointment',
    defaultIntervals: ['daily'],
  },

  // ── Troubleshooting ───────────────────────────────────────────────────────
  'patient.troubleshooting.ticket-receipt': {
    key: 'patient.troubleshooting.ticket-receipt',
    referenceCode: 'Troubleshooting_01',
    label: 'Troubleshooting Ticket Receipt',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.TROUBLESHOOTING,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient receives confirmation of support ticket creation',
    defaultIntervals: ['immediately'],
    essential: true,
  },
  'patient.troubleshooting.ticket-update': {
    key: 'patient.troubleshooting.ticket-update',
    referenceCode: 'Troubleshooting_02',
    label: 'Troubleshooting Ticket Update',
    audience: NotificationAudience.PATIENT,
    category: NotificationCategory.TROUBLESHOOTING,
    channels: [...PATIENT_IN_APP_EMAIL_SMS],
    description: 'Patient receives update on support ticket',
    defaultIntervals: ['immediately'],
  },
} as const satisfies Record<string, NotificationTypeDefinition>;

// ─── Combined registry & helpers ────────────────────────────────────────────

export const ALL_NOTIFICATIONS = {
  ...PROVIDER_NOTIFICATIONS,
  ...PATIENT_NOTIFICATIONS,
} as const;

export type ProviderNotificationKey = keyof typeof PROVIDER_NOTIFICATIONS;
export type PatientNotificationKey = keyof typeof PATIENT_NOTIFICATIONS;
export type NotificationKey = ProviderNotificationKey | PatientNotificationKey;

export function getNotificationDef(key: NotificationKey): NotificationTypeDefinition {
  return ALL_NOTIFICATIONS[key];
}

export function getNotificationsByAudience(audience: NotificationAudience): NotificationTypeDefinition[] {
  return Object.values(ALL_NOTIFICATIONS).filter((n) => n.audience === audience);
}

export function getNotificationsByCategory(category: NotificationCategory): NotificationTypeDefinition[] {
  return Object.values(ALL_NOTIFICATIONS).filter((n) => n.category === category);
}

export function getNotificationsByAudienceAndCategory(
  audience: NotificationAudience,
  category: NotificationCategory
): NotificationTypeDefinition[] {
  return Object.values(ALL_NOTIFICATIONS).filter((n) => n.audience === audience && n.category === category);
}
